import React, { useCallback, useMemo, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Button from "../Buttons/Button";
import {
  DeleteIcon,
  PrintIcon,
  ShareIcon,
} from "../../../assets/icons/iconsProvider";
import {
  buttonNameConstants,
  buttonTypeConstants,
  storeConstants,
} from "../../../constants";
import AgGridPaginationComponent from "./AgGridPaginationComponent";
import AGGridToolTip from "../toolTipComponent/AGGridToolTip";
import CustomGridHeader from "./CustomGridHeader";
import { isTransferInventoryEnabled } from "../../utility";
import { userActions } from "../../../redux/actions";
import { connect } from "react-redux";
import { useColumnSizingPersistence } from "../../hooks/useColumnSizingPersistence";

const ServerPaginationTable = ({
  colDefs,
  gridRef,
  gridData,
  gridState,
  getRowStyle,
  tableHeight,
  selectedRows,
  onCellClicked,
  updateGridState,
  components = null,
  rowClassRules = {},
  defaultColDef = null,
  pagination = true,
  onSelectionChanged,
  animateRows = true,
  getRowId = () => {},
  editType = "fullRow",
  tooltipShowDelay = 0,
  handleDeleteAllClick,
  handlePrintLabelClick,
  isDataLoading = false,
  singleClickEdit = true,
  handleTransferInventory,
  tooltipHideDelay = 1000,
  tooltipInteraction = true,
  suppressCellFocus = true,
  rowSelection = "multiple",
  isDisablePaginationActions,
  alwaysShowVerticalScroll = false,
  suppressRowClickSelection = true,
  suppressRowHoverHighlight = true,
  alwaysShowHorizontalScroll = false,
  suppressDragLeaveHidesColumns = true,
  defaultGridSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  currentStoreCompany = {},
  stores = [],
  scrollRef = { current: 0 },
  isInlineEditRefresh = false,
  setIsInlineEditRefresh,
  inlineEditRefreshScrollValue = 0,
  user,
  tableId,
  updateUserInformation,
  store,
}) => {
  var fromEdit = false;
  var fromEditScrollValue = 0;
  const savedColumnSizing =
    user?.userPreferences?.tables?.[tableId]?.columnSizing;

  const [columnSizing, setColumnSizing] = useState({});

  // Persist column sizing changes to user preferences
  useColumnSizingPersistence({
    tableId,
    columnSizing,
    user,
    store,
    updateUserInformation,
    enabled: true,
  });

  useEffect(() => {
    const applySavedSizing = () => {
      const api = gridRef?.current?.api;
      if (!api) return;
      const allColumns = api.getColumns && api.getColumns();
      if (!allColumns || allColumns.length === 0) return;

      const columnApi = gridRef?.current?.columnApi;

      if (savedColumnSizing) {
        const columnState = Object.entries(savedColumnSizing).map(
          ([colId, width]) => ({ colId, width })
        );

        columnApi?.applyColumnState({ state: columnState });
      } else {
        api.sizeColumnsToFit();
      }
    };

    // Try to apply immediately, otherwise retry shortly to wait for columns
    applySavedSizing();
    const retry = setTimeout(() => applySavedSizing(), 50);
    return () => clearTimeout(retry);
  }, [savedColumnSizing, gridRef]);

  const onFirstDataRendered = useCallback(
    (params) => {
      if (savedColumnSizing) {
        const columnState = Object.entries(savedColumnSizing).map(
          ([colId, width]) => ({
            colId,
            width,
          })
        );
        params.columnApi.applyColumnState({ state: columnState });
      } else {
        params.api.sizeColumnsToFit();
        console.log("columnState1");
      }

      params.api.gridBodyCtrl.eBodyViewport.scrollTop = scrollRef.current;

      fromEdit = true;
      fromEditScrollValue = scrollRef.current;
    },
    [savedColumnSizing]
  );
  const onModelUpdated = useCallback(
    (params) => {
      if (fromEdit) {
        if (
          params.api.gridBodyCtrl.eBodyViewport.scrollHeight -
            params.api.gridBodyCtrl.eBodyViewport.clientHeight >
          fromEditScrollValue
        ) {
          params.api.ensureIndexVisible(gridState.pageSize, "middle");
          params.api.gridBodyCtrl.eBodyViewport.scrollTop = fromEditScrollValue;
          fromEdit = false;
          fromEditScrollValue = 0;
        }
      } else if (isInlineEditRefresh) {
        if (
          params.api.gridBodyCtrl.eBodyViewport.scrollHeight -
            params.api.gridBodyCtrl.eBodyViewport.clientHeight >
          inlineEditRefreshScrollValue
        ) {
          params.api.ensureIndexVisible(gridState.pageSize, "middle");
          params.api.gridBodyCtrl.eBodyViewport.scrollTop =
            inlineEditRefreshScrollValue;
          setIsInlineEditRefresh?.(false);
        }
      }
    },
    [isInlineEditRefresh]
  );
  const onSortChanged = () => {
    scrollRef.current = 0;
    gridRef.current.api.gridBodyCtrl.eBodyViewport.scrollTop = 0;
  };

  const agGridDefaultColDef = useMemo(() => {
    return {
      cellStyle: { display: "flex", alignItems: "center" },
      resizable: true,
      tooltipComponent: AGGridToolTip,
      headerComponentParams: {
        gridState: gridState,
        updateGridState: updateGridState,
      },
      minWidth: storeConstants.GRID_MIN_COLUMN_WIDTH,
      maxWidth: storeConstants.GRID_MAX_COLUMN_WIDTH,
    };
  }, [gridState]);

  const agGridComponents = useMemo(() => {
    return {
      agColumnHeader: CustomGridHeader,
    };
  }, []);

  const onGridSizeChanged = useCallback(
    (params) => {
      var gridWidth = document.querySelector(".ag-body-viewport").clientWidth;
      var columnsToShow = [];
      var columnsToHide = [];
      var totalColsWidth = 0;
      var allColumns = params.api.getColumns();
      if (allColumns && allColumns.length > 0) {
        for (var i = 0; i < allColumns.length; i++) {
          var column = allColumns[i];
          totalColsWidth += column.getMinWidth() || 0;
          if (totalColsWidth > gridWidth) {
            columnsToHide.push(column.getColId());
          } else {
            columnsToShow.push(column.getColId());
          }
        }
      }
      params.api.setColumnsVisible(columnsToShow, true);
      params.api.setColumnsVisible(columnsToHide, false);
      if (!savedColumnSizing) {
        window.setTimeout(() => {
          params.api.sizeColumnsToFit();
        }, 10);
      }
    },
    [window, savedColumnSizing]
  );

  return (
    <div
      className="ag-theme-quartz" // applying the grid theme
      style={{
        height: tableHeight,
        paddingBottom: "5px",
      }} // the grid will fill the size of the parent container
    >
      {selectedRows?.length > 0 && (
        <div
          className={`d-flex justify-content-end gap-2 ${
            handlePrintLabelClick ? "py-2" : ""
          } `}
        >
          {handlePrintLabelClick && (
            <Button
              label={buttonNameConstants.DELETE}
              buttonType={buttonTypeConstants.RED_BUTTON}
              IconImage={DeleteIcon}
              type="button"
              handleClick={() => handleDeleteAllClick()}
            />
          )}
          {isTransferInventoryEnabled(currentStoreCompany, stores) && (
            <Button
              label={buttonNameConstants.TRANSFER}
              buttonType={buttonTypeConstants.GHOST_BUTTON}
              IconImage={ShareIcon}
              type="button"
              handleClick={() => handleTransferInventory()}
            />
          )}
          {handlePrintLabelClick && (
            <Button
              label={buttonNameConstants.PRINT_LABEL}
              type="button"
              IconImage={PrintIcon}
              buttonType={buttonTypeConstants.LIGHT_PURPLE}
              handleClick={() => handlePrintLabelClick()}
            />
          )}
        </div>
      )}
      <AgGridReact
        ref={gridRef}
        rowData={gridData}
        getRowId={getRowId}
        editType={editType}
        columnDefs={colDefs}
        animateRows={animateRows}
        getRowStyle={getRowStyle}
        rowSelection={rowSelection}
        rowClassRules={rowClassRules}
        onCellClicked={onCellClicked}
        singleClickEdit={singleClickEdit}
        tooltipShowDelay={tooltipShowDelay}
        tooltipHideDelay={tooltipHideDelay}
        suppressCellFocus={suppressCellFocus}
        onGridSizeChanged={onGridSizeChanged}
        onSelectionChanged={onSelectionChanged}
        tooltipInteraction={tooltipInteraction}
        onFirstDataRendered={onFirstDataRendered}
        autoSizeStrategy={defaultGridSizeStrategy}
        components={components || agGridComponents}
        alwaysShowVerticalScroll={alwaysShowVerticalScroll}
        defaultColDef={
          defaultColDef
            ? { ...agGridDefaultColDef, ...defaultColDef, resizable: true }
            : agGridDefaultColDef
        }
        suppressRowClickSelection={suppressRowClickSelection}
        suppressRowHoverHighlight={suppressRowHoverHighlight}
        alwaysShowHorizontalScroll={alwaysShowHorizontalScroll}
        suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
        onBodyScroll={(event) => {
          scrollRef.current = event.top;
        }}
        onModelUpdated={onModelUpdated}
        onSortChanged={onSortChanged}
        onColumnResized={(params) => {
          if (params.source === "uiColumnResized") {
            const columnState = params.api.getColumnState();

            const newColumnSizing = {};
            columnState.forEach((col) => {
              newColumnSizing[col.colId] = col.width;
            });

            setColumnSizing(newColumnSizing);
          }
        }}
      />
      {pagination && (
        <AgGridPaginationComponent
          gridData={gridData}
          gridState={gridState}
          isDataLoading={isDataLoading}
          updateGridState={updateGridState}
          isDisablePaginationActions={isDisablePaginationActions}
          gridRef={gridRef}
          scrollRef={scrollRef}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
  store: state.store,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  updateUserInformation: (data, isSystemUser) =>
    dispatch(userActions.updateUserInformation(data, isSystemUser)),
});

//-------Export Login Component
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ServerPaginationTable);
